import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { 
  Wallet, Smartphone, CreditCard, CheckCircle2, ArrowRight, 
  Crown, Sparkles, Shield, Gift, Coins, Zap, Lock, AlertCircle,
  Loader2, ExternalLink, Copy, Timer, Receipt, Activity, Bitcoin
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import RechargeModal from "../components/wallet/RechargeModal";
import VipPurchaseModal from "../components/wallet/VipPurchaseModal";
import VoucherModal from "../components/wallet/VoucherModal";
import TransactionHistory from "../components/wallet/TransactionHistory";

const PACKAGES = [
  { id: "starter", name: "Starter Pack", coins: 500, bonus_coins: 0, price_eur: 4.99, color: "#3b82f6" },
  { id: "gamer", name: "Gamer Pack", coins: 1200, bonus_coins: 100, price_eur: 9.99, color: "#10b981", popular: true },
  { id: "pro", name: "Pro Pack", coins: 2600, bonus_coins: 400, price_eur: 19.99, color: "#8b5cf6", badge: "+15% BONUS" },
  { id: "elite", name: "Elite Pack", coins: 7000, bonus_coins: 1500, price_eur: 49.99, color: "#f59e0b", badge: "+21% BONUS" },
  { id: "founder", name: "Founder Pack", coins: 15000, bonus_coins: 4500, price_eur: 99.99, color: "#ef4444", badge: "+30% BEST VALUE" },
];

export default function Donate() {
  const { user, setUser } = useAuth();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "recharge"; // recharge | vip | voucher | history

  const [tab, setTab] = useState(initialTab);
  const [packages, setPackages] = useState(PACKAGES);
  const [settings, setSettings] = useState(null);
  const [balance, setBalance] = useState(null);
  const [modal, setModal] = useState(null); // recharge | vip | voucher

  // Load balance & settings
  useEffect(() => {
    if (user) {
      api.get("/wallet/balance").then(r => setBalance(r.data)).catch(() => {});
    }
    api.get("/wallet/packages").then(r => {
      if (r.data && r.data.length > 0) setPackages(r.data);
    }).catch(() => {});

    api.get("/wallet/settings").then(r => {
      if (r.data) setSettings(r.data);
    }).catch(() => {});
  }, [user]);

  const idgCoins = balance?.idg_coins ?? user?.idg_coins ?? 0;
  const vipTier = balance?.vip_tier ?? user?.vip_tier ?? "none";
  const vipUntil = balance?.vip_until ?? user?.vip_until;

  const vipTiers = [
    {
      id: "bronze",
      name: "VIP BRONZE",
      coins: 500,
      period: "30 Zile",
      color: "#cd7f32",
      features: [
        "Tag [VIP] pe serverele de joc",
        "Slot rezervat la conectare (Connect VIP)",
        "Stiluri de nume animate pe forum",
        "+500 Puncte lunare de activitate",
      ]
    },
    {
      id: "gold",
      name: "VIP GOLD",
      coins: 1000,
      period: "90 Zile",
      color: "#f59e0b",
      badge: "POPULAR",
      features: [
        "Toate avantajele VIP Bronze",
        "Arme & Skin-uri exclusive în Loadout",
        "Cadre VIP animate pentru avatar",
        "Dublu multiplicator de reputație",
        "Acces în camera privată Staff Chat",
      ]
    },
    {
      id: "diamond",
      name: "DONATOR DIAMOND",
      coins: 2500,
      period: "Permanent",
      color: "#00e5ff",
      badge: "BEST VALUE",
      features: [
        "Toate avantajele VIP Gold Permanent",
        "Rol & Culoare unică pe Discord",
        "Imunitate la kick/slap pe servere",
        "Acces la toate Case Openings gratuite",
        "Insignă fondator donator pe profil",
      ]
    },
  ];

  const handleRechargeSuccess = (newBalance) => {
    api.get("/wallet/balance").then(r => setBalance(r.data)).catch(() => {});
    if (setUser && newBalance !== null && newBalance !== undefined) {
      setUser(u => ({ ...u, idg_coins: newBalance }));
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto space-y-6" data-testid="donate-page">
      
      {/* ─── Hero Command Header ─── */}
      <div className="bg-[#0b0e15] border border-white/10 p-6 flex flex-col md:flex-row md:items-center justify-between gap-6 relative overflow-hidden shadow-2xl">
        <div className="absolute top-0 right-0 w-96 h-full bg-gradient-to-l from-amber-500/10 via-transparent to-transparent pointer-events-none" />
        
        <div className="flex items-center gap-4 relative z-10">
          <div className="w-12 h-12 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400 shrink-0 shadow-lg">
            <Coins className="w-6 h-6" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.5 text-[9px] font-chakra font-black uppercase tracking-wider bg-amber-500 text-black">
                INDUNGI MONETIZARE & VIP
              </span>
              <span className="text-[10px] font-mono text-emerald-400 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                Gateway Activ
              </span>
            </div>
            <h1 className="font-chakra text-2xl sm:text-3xl font-black uppercase tracking-wider text-white mt-1">
              Portofel, Plăți & Servicii VIP
            </h1>
            <p className="text-xs text-zinc-400 font-mono">
              Cumpără IDG Coins instant cu Card, PayPal, Revolut, Crypto sau PaysafeCard.
            </p>
          </div>
        </div>

        {/* User Balance Overview */}
        {user ? (
          <div className="flex items-center gap-4 bg-black/50 border border-white/10 p-3.5 rounded-lg relative z-10 shrink-0">
            <div className="space-y-0.5">
              <div className="text-[10px] font-chakra font-black uppercase text-zinc-400">Soldul Tău Actual</div>
              <div className="flex items-baseline gap-1.5">
                <span className="font-mono text-2xl font-black text-amber-400">{idgCoins.toLocaleString()}</span>
                <span className="text-xs font-mono text-zinc-500">IDG Coins</span>
              </div>
              <div className="text-[10px] font-mono text-zinc-500">
                VIP: <strong className="text-amber-400 uppercase">{vipTier}</strong> {vipUntil && `(până la ${vipUntil.slice(0, 10)})`}
              </div>
            </div>
            <button
              onClick={() => setModal("recharge")}
              className="tf-btn-primary px-4 py-2.5 text-xs font-black uppercase flex items-center gap-1.5 shadow-[0_0_15px_rgba(245,158,11,0.3)]"
            >
              <Coins className="w-3.5 h-3.5" />
              <span>Reîncarcă</span>
            </button>
          </div>
        ) : (
          <Link
            to="/login"
            className="tf-btn-primary px-6 py-3 text-xs font-black uppercase relative z-10"
          >
            Autentificare pentru Reîncărcare
          </Link>
        )}
      </div>

      {/* ─── Navigation Tabs ─── */}
      <div className="flex gap-2 border-b border-white/10 pb-0 overflow-x-auto">
        <button
          onClick={() => setTab("recharge")}
          className={`flex items-center gap-2 px-4 py-3 text-xs font-chakra font-black uppercase tracking-wider border-b-2 transition-all -mb-[1px] ${
            tab === "recharge"
              ? "border-amber-500 text-white bg-white/[0.04]"
              : "border-transparent text-zinc-500 hover:text-zinc-300"
          }`}
        >
          <Coins className="w-4 h-4 text-amber-400" />
          Cumpără IDG Coins
        </button>

        <button
          onClick={() => setTab("vip")}
          className={`flex items-center gap-2 px-4 py-3 text-xs font-chakra font-black uppercase tracking-wider border-b-2 transition-all -mb-[1px] ${
            tab === "vip"
              ? "border-amber-500 text-white bg-white/[0.04]"
              : "border-transparent text-zinc-500 hover:text-zinc-300"
          }`}
        >
          <Crown className="w-4 h-4 text-amber-400" />
          Abonamente VIP
        </button>

        <button
          onClick={() => setTab("voucher")}
          className={`flex items-center gap-2 px-4 py-3 text-xs font-chakra font-black uppercase tracking-wider border-b-2 transition-all -mb-[1px] ${
            tab === "voucher"
              ? "border-amber-500 text-white bg-white/[0.04]"
              : "border-transparent text-zinc-500 hover:text-zinc-300"
          }`}
        >
          <Gift className="w-4 h-4 text-violet-400" />
          Revendică Voucher PIN
        </button>

        {user && (
          <button
            onClick={() => setTab("history")}
            className={`flex items-center gap-2 px-4 py-3 text-xs font-chakra font-black uppercase tracking-wider border-b-2 transition-all -mb-[1px] ${
              tab === "history"
                ? "border-amber-500 text-white bg-white/[0.04]"
                : "border-transparent text-zinc-500 hover:text-zinc-300"
            }`}
          >
            <Receipt className="w-4 h-4 text-zinc-400" />
            Istoric Tranzacții
          </button>
        )}
      </div>

      {/* ─── TAB 1: CUMPĂRĂ IDG COINS ─── */}
      {tab === "recharge" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-chakra font-black text-lg uppercase tracking-wider text-white">
                Pachete Disponibile IDG Coins
              </h2>
              <p className="text-xs text-zinc-400 font-mono">
                Selectează un pachet pentru a deschide gateway-ul de plată instantanee.
              </p>
            </div>
            <div className="flex items-center gap-2 text-xs font-mono text-zinc-400">
              <Shield className="w-4 h-4 text-emerald-400" /> Plăți Securizate 256-bit SSL
            </div>
          </div>

          {/* Packages Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {packages.map(pkg => (
              <div
                key={pkg.id}
                className="bg-[#0b0e15] border rounded-lg p-5 flex flex-col justify-between relative transition-all duration-200 hover:scale-[1.02] group"
                style={{
                  background: `linear-gradient(135deg, ${pkg.color}08, transparent)`,
                  borderColor: `${pkg.color}40`
                }}
              >
                {pkg.popular && (
                  <div className="absolute -top-2.5 right-3 px-2 py-0.5 text-[9px] font-black font-chakra uppercase tracking-wider rounded-full shadow-md"
                    style={{ background: pkg.color, color: "#000" }}>
                    POPULAR
                  </div>
                )}
                {pkg.badge && !pkg.popular && (
                  <div className="text-[9px] font-mono font-bold mb-2 px-1.5 py-0.5 rounded inline-block"
                    style={{ background: `${pkg.color}25`, color: pkg.color }}>
                    {pkg.badge}
                  </div>
                )}

                <div>
                  <div className="font-chakra font-black text-white text-base uppercase tracking-wide">{pkg.name}</div>
                  <div className="flex items-baseline gap-1 mt-3">
                    <span className="font-mono text-3xl font-black" style={{ color: pkg.color }}>
                      {(pkg.total_coins ?? pkg.coins).toLocaleString()}
                    </span>
                    <span className="text-xs text-zinc-500 font-mono">IDG</span>
                  </div>
                  {pkg.bonus_coins > 0 && (
                    <div className="text-[10px] text-zinc-400 font-mono mt-1">
                      {pkg.coins?.toLocaleString()} + <strong style={{ color: pkg.color }}>{pkg.bonus_coins?.toLocaleString()} bonus</strong>
                    </div>
                  )}
                </div>

                <div className="mt-6 pt-4 border-t border-white/10 flex items-center justify-between">
                  <div className="font-mono font-black text-white text-xl">€{pkg.price_eur ?? pkg.price}</div>
                  <button
                    onClick={() => setModal("recharge")}
                    className="px-4 py-2 rounded font-chakra font-black text-xs uppercase tracking-wider text-black transition-all group-hover:scale-105"
                    style={{ backgroundColor: pkg.color }}
                  >
                    Cumpără
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Payment Methods Badges */}
          <div className="bg-[#0b0e15] border border-white/10 rounded-lg p-5">
            <h3 className="font-chakra font-black text-xs uppercase tracking-widest text-zinc-400 mb-3">
              Metode de Plată Acceptate cu Detecție Automată
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <div className="p-3 rounded-lg bg-blue-500/5 border border-blue-500/20 flex items-center gap-3">
                <CreditCard className="w-5 h-5 text-blue-400" />
                <div>
                  <div className="font-chakra font-bold text-xs text-white uppercase">Card Bancar</div>
                  <div className="text-[10px] font-mono text-zinc-400">Visa / Mastercard (Direct)</div>
                </div>
              </div>

              <div className="p-3 rounded-lg bg-[#0079C1]/5 border border-[#0079C1]/20 flex items-center gap-3">
                <Wallet className="w-5 h-5 text-[#0079C1]" />
                <div>
                  <div className="font-chakra font-bold text-xs text-white uppercase">PayPal</div>
                  <div className="text-[10px] font-mono text-zinc-400">Instant / PayPal.Me</div>
                </div>
              </div>

              <div className="p-3 rounded-lg bg-[#ee1177]/5 border border-[#ee1177]/20 flex items-center gap-3">
                <Smartphone className="w-5 h-5 text-[#ee1177]" />
                <div>
                  <div className="font-chakra font-bold text-xs text-white uppercase">Revolut</div>
                  <div className="text-[10px] font-mono text-zinc-400">Transfer @Revtag</div>
                </div>
              </div>

              <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/20 flex items-center gap-3">
                <Bitcoin className="w-5 h-5 text-amber-400" />
                <div>
                  <div className="font-chakra font-bold text-xs text-white uppercase">Crypto</div>
                  <div className="text-[10px] font-mono text-zinc-400">USDT TRC20 / BTC</div>
                </div>
              </div>

              <div className="p-3 rounded-lg bg-[#f97316]/5 border border-[#f97316]/20 flex items-center gap-3">
                <Gift className="w-5 h-5 text-[#f97316]" />
                <div>
                  <div className="font-chakra font-bold text-xs text-white uppercase">PaysafeCard</div>
                  <div className="text-[10px] font-mono text-zinc-400">PIN Instant 10/25/50€</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── TAB 2: ABONAMENTE VIP ─── */}
      {tab === "vip" && (
        <div className="space-y-6">
          <div>
            <h2 className="font-chakra font-black text-lg uppercase tracking-wider text-white">
              Abonamente & Pachete VIP
            </h2>
            <p className="text-xs text-zinc-400 font-mono">
              Deblochează beneficii unice în joc, pe forum și în comunitate. Activare instantanee cu IDG Coins.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {vipTiers.map(tier => (
              <div 
                key={tier.id}
                className={`bg-[#0b0e15] border p-6 flex flex-col justify-between relative rounded-lg transition-all duration-200 hover:border-amber-500/70 ${
                  tier.badge ? 'border-amber-500 shadow-[0_0_20px_rgba(245,158,11,0.15)]' : 'border-white/10'
                }`}
              >
                {tier.badge && (
                  <span 
                    className="absolute -top-3 right-4 px-2.5 py-0.5 text-black font-black font-chakra text-[10px] uppercase tracking-wider shadow-md rounded-full"
                    style={{ backgroundColor: tier.color }}
                  >
                    {tier.badge}
                  </span>
                )}

                <div>
                  <h3 className="font-chakra font-black text-xl uppercase tracking-wider text-white mb-2" style={{ color: tier.color }}>
                    {tier.name}
                  </h3>
                  
                  <div className="flex items-baseline gap-1.5 my-4">
                    <span className="font-mono font-black text-3xl text-white">{tier.coins.toLocaleString()}</span>
                    <span className="font-mono text-xs text-zinc-400">IDG Coins / {tier.period}</span>
                  </div>

                  <div className="space-y-2.5 my-6">
                    {tier.features.map((feat, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-xs font-mono text-zinc-300">
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                        <span>{feat}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <button
                  onClick={() => setModal("vip")}
                  className="w-full py-3 rounded bg-white/5 hover:bg-amber-500 hover:text-black border border-white/10 hover:border-amber-500 text-white text-center text-xs font-black font-chakra uppercase tracking-wider transition-all shadow-sm"
                >
                  Activează {tier.name}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ─── TAB 3: VOUCHER PIN ─── */}
      {tab === "voucher" && (
        <div className="bg-[#0b0e15] border border-white/10 rounded-lg p-6 max-w-lg mx-auto space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-violet-500/10 border border-violet-500/30 flex items-center justify-center text-violet-400">
              <Gift className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-chakra font-black text-base uppercase text-white">Revendică Cod Voucher</h3>
              <p className="text-xs text-zinc-400 font-mono">Introdu codul PIN primit cadou pentru a adăuga monede în portofel.</p>
            </div>
          </div>

          <button
            onClick={() => setModal("voucher")}
            className="w-full py-3 rounded-lg font-chakra font-black text-xs uppercase tracking-widest text-white bg-violet-600 hover:bg-violet-500 transition-all shadow-[0_0_20px_rgba(139,92,246,0.3)]"
          >
            Deschide Formularul de Revendicare PIN
          </button>
        </div>
      )}

      {/* ─── TAB 4: ISTORIC TRANZACȚII ─── */}
      {tab === "history" && (
        <div className="bg-[#0b0e15] border border-white/10 rounded-lg p-6">
          <TransactionHistory />
        </div>
      )}

      {/* ─── Modals (Recharge, VIP, Voucher) ─── */}
      {modal === "recharge" && (
        <RechargeModal
          onClose={() => setModal(null)}
          onSuccess={handleRechargeSuccess}
        />
      )}

      {modal === "vip" && (
        <VipPurchaseModal
          userCoins={idgCoins}
          userVipTier={vipTier}
          onClose={() => setModal(null)}
          onSuccess={handleRechargeSuccess}
        />
      )}

      {modal === "voucher" && (
        <VoucherModal
          onClose={() => setModal(null)}
          onSuccess={handleRechargeSuccess}
        />
      )}

    </div>
  );
}
